"use client"

import React from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { useTestSetsList } from "@/test-set/catalog"
import { useScopedStorageKey } from "@/workspaces/scope"
import {
  Input,
  RadioGroup,
  RadioGroupItem,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"

import { EntitySelectionCard } from "@/components/entity-picker/EntitySelectionCard"
import type { JsonObject } from "@/types/json"

import { TestCaseLibraryPicker } from "./TestCaseLibraryPicker"
import { TestCasePicker } from "./TestCasePicker"
import { TestSelectionPickerDialog } from "./TestSelectionPickerDialog"
import {
  getInitialTestCaseMode,
  getSubSampleInputBlurResult,
  getSubSampleInputChangeResult,
  getSyncedTestCaseMode,
  getTestCaseModeTransition,
  TEST_CASE_MODE,
  type TestCaseMode,
} from "./testSetSelectorState"

const TestSetIcon = ENTITY_ICONS["test-sets"]

interface SimpleTestSetFeatureProps {
  testSetIds?: string[]
  shouldSubSample?: boolean
  subSampleSize?: number
  testCaseParamsByIteration?: JsonObject[]
  disabled?: boolean
  singleSelect?: boolean
  onTestSetChange?: (testSetIds: string[]) => void
  onSubSamplingChange?: (
    shouldSubSample: boolean,
    subSampleSize?: number,
  ) => void
  selectedTestCaseIds?: string[]
  onSelectedTestCaseIdsChange?: (ids: string[]) => void
  onTestCaseParamsChange?: (params: JsonObject[]) => void
  onValidationChange?: (isValid: boolean, errors: string[]) => void
  tagColorMap?: Record<string, string | null | undefined>
  /**
   * Optional action offered inside the test set picker, for a caller that can
   * create one, so a user with no test sets is not stuck at an empty catalog.
   */
  footer?: React.ReactNode
}

export function TestSetSelector(props: SimpleTestSetFeatureProps) {
  const scope = useScopedStorageKey("test-case-selection-mode")
  return <TestSelection key={scope} {...props} />
}

function TestSelection(props: SimpleTestSetFeatureProps) {
  const { data: testSets, isLoading } = useTestSetsList()
  const showCases = (props.selectedTestCaseIds?.length ?? 0) > 0
  const [isOpen, setIsOpen] = React.useState(false)
  const [view, setView] = React.useState("sets")
  const [draftSets, setDraftSets] = React.useState<string[]>([])
  const [draftCases, setDraftCases] = React.useState<string[]>([])
  const openPicker = () => {
    if (props.disabled) {
      return
    }
    setDraftSets([...(props.testSetIds ?? [])])
    setDraftCases([...(props.selectedTestCaseIds ?? [])])
    setView(showCases ? "cases" : "sets")
    setIsOpen(true)
  }
  const cancelPicker = () => setIsOpen(false)
  const applyCases = (ids: string[], testSetIds: string[]) => {
    if (ids.length > 0) {
      props.onTestSetChange?.(testSetIds)
    }
    props.onSelectedTestCaseIdsChange?.(ids)
    props.onSubSamplingChange?.(false, undefined)
    setIsOpen(false)
  }
  const pickerBanner = (
    <Tabs value={view} onValueChange={setView}>
      <TabsList variant="pill" aria-label="Select tests by">
        <TabsTrigger variant="pill" value="sets">
          Test Sets
        </TabsTrigger>
        <TabsTrigger variant="pill" value="cases">
          Test Cases
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )

  return (
    <>
      {!showCases && <TestSetSelection {...props} onBrowse={openPicker} />}
      <TestCaseLibraryPicker
        selectedIds={props.selectedTestCaseIds ?? []}
        disabled={props.disabled}
        singleSelect={props.singleSelect}
        hideCard={!showCases}
        hidePicker
        onBrowse={openPicker}
        onChange={applyCases}
      />
      <TestSelectionPickerDialog
        isOpen={isOpen && !props.disabled}
        view={view}
        onCancel={cancelPicker}
        onApplySets={() => {
          props.onTestSetChange?.(draftSets)
          if (showCases) {
            props.onSelectedTestCaseIdsChange?.([])
            props.onSubSamplingChange?.(false, undefined)
          }
          setIsOpen(false)
        }}
        testSets={testSets ?? []}
        isLoading={isLoading}
        draftSets={draftSets}
        onSetsChange={setDraftSets}
        draftCases={draftCases}
        onCasesChange={setDraftCases}
        onApplyCases={applyCases}
        singleSelect={props.singleSelect}
        tagColorMap={props.tagColorMap}
        createAction={props.footer}
        banner={pickerBanner}
      />
    </>
  )
}

function TestSetSelection({
  testSetIds = [],
  shouldSubSample = false,
  subSampleSize,
  disabled = false,
  singleSelect = false,
  onTestSetChange,
  onSubSamplingChange,
  selectedTestCaseIds = [],
  onSelectedTestCaseIdsChange,
  onBrowse,
}: SimpleTestSetFeatureProps & {
  onBrowse: () => void
}) {
  const { data: testSetRecords, isLoading: testSetsLoading } = useTestSetsList()

  const hasTestSets = testSetIds.length > 0
  const hasMultipleTestSets = testSetIds.length > 1
  const firstTestSetId = testSetIds[0] || ""
  const hasSpecificTestCases = selectedTestCaseIds.length > 0

  const handleTestSetValuesChange = React.useCallback(
    (ids: string[]) => {
      const next = singleSelect ? ids.slice(-1) : ids
      onTestSetChange?.(next)
    },
    [singleSelect, onTestSetChange],
  )

  // Chips follow the committed selection order, and an id with no matching
  // record still gets one so it can be removed.
  const chips = React.useMemo(
    () =>
      testSetIds.map((id) => {
        const testSet = (testSetRecords ?? []).find((ts) => ts.id === id)
        return {
          id,
          label: testSet?.display_name || id,
          isMissing: !testSet && !testSetsLoading,
        }
      }),
    [testSetIds, testSetRecords, testSetsLoading],
  )

  const handleRemoveTestSet = React.useCallback(
    (testSetId: string) => {
      handleTestSetValuesChange(testSetIds.filter((id) => id !== testSetId))
    },
    [handleTestSetValuesChange, testSetIds],
  )

  const testCaseModeLabelId = React.useId()
  const [testCaseMode, setTestCaseMode] = React.useState<TestCaseMode>(() =>
    getInitialTestCaseMode(hasSpecificTestCases, shouldSubSample),
  )

  React.useEffect(() => {
    setTestCaseMode((currentMode) => {
      const nextMode = getSyncedTestCaseMode(
        currentMode,
        hasSpecificTestCases,
        shouldSubSample,
      )
      if (
        nextMode === TEST_CASE_MODE.ALL &&
        currentMode !== TEST_CASE_MODE.ALL
      ) {
        onSelectedTestCaseIdsChange?.([])
        onSubSamplingChange?.(false, undefined)
      }
      return nextMode
    })
  }, [
    hasSpecificTestCases,
    shouldSubSample,
    onSelectedTestCaseIdsChange,
    onSubSamplingChange,
  ])

  const handleTestCaseModeChange = React.useCallback(
    (mode: string) => {
      const nextMode = mode as TestCaseMode
      setTestCaseMode(nextMode)
      const transition = getTestCaseModeTransition(nextMode, subSampleSize)
      if (transition.shouldClearSelectedTestCaseIds) {
        onSelectedTestCaseIdsChange?.([])
      }
      onSubSamplingChange?.(
        transition.subSamplingUpdate.shouldSubSample,
        transition.subSamplingUpdate.subSampleSize,
      )
    },
    [onSubSamplingChange, onSelectedTestCaseIdsChange, subSampleSize],
  )

  const [subSampleInputValue, setSubSampleInputValue] = React.useState(
    subSampleSize?.toString() || "",
  )

  React.useEffect(() => {
    setSubSampleInputValue(subSampleSize?.toString() || "")
  }, [subSampleSize])

  const handleSubSampleSizeChange = (value: string) => {
    const result = getSubSampleInputChangeResult(value)
    setSubSampleInputValue(result.cleanValue)
    if (result.subSamplingUpdate) {
      onSubSamplingChange?.(
        result.subSamplingUpdate.shouldSubSample,
        result.subSamplingUpdate.subSampleSize,
      )
    }
  }

  const handleSubSampleSizeBlur = () => {
    const result = getSubSampleInputBlurResult(subSampleInputValue)
    if (!result) {
      return
    }
    setSubSampleInputValue(result.nextInputValue)
    onSubSamplingChange?.(
      result.subSamplingUpdate.shouldSubSample,
      result.subSamplingUpdate.subSampleSize,
    )
  }

  return (
    <>
      <EntitySelectionCard
        icon={TestSetIcon}
        title="Test Set"
        chips={chips}
        onRemove={handleRemoveTestSet}
        onBrowse={onBrowse}
        onClearAll={() => handleTestSetValuesChange([])}
        browseLabel="Browse Test Sets"
        addLabel="Add Test Sets"
        browseTestId="test-set-browse-button"
        disabled={disabled}
      >
        {/* Nothing to scope until there is a test set to scope. */}
        {hasTestSets && (
          <div className="flex flex-col gap-2">
            <span
              id={testCaseModeLabelId}
              className="text-sm font-medium text-coval-text"
            >
              Test Cases
            </span>
            <RadioGroup
              aria-labelledby={testCaseModeLabelId}
              value={hasMultipleTestSets ? TEST_CASE_MODE.ALL : testCaseMode}
              onValueChange={handleTestCaseModeChange}
              disabled={disabled || hasMultipleTestSets}
            >
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value={TEST_CASE_MODE.ALL} />
                Run all test cases
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem
                  value={TEST_CASE_MODE.SELECT}
                  disabled={hasMultipleTestSets}
                />
                Select specific test cases
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value={TEST_CASE_MODE.SUBSAMPLE} />
                Randomly subsample
              </label>
            </RadioGroup>

            {testCaseMode === TEST_CASE_MODE.SUBSAMPLE && (
              <div className="space-y-1">
                <Input
                  type="number"
                  placeholder="Number of test cases"
                  value={subSampleInputValue}
                  onChange={(e) => handleSubSampleSizeChange(e.target.value)}
                  onBlur={handleSubSampleSizeBlur}
                  disabled={disabled}
                  aria-label="Sample size"
                />
                <p className="text-xs text-coval-text-muted">
                  Random subset of test cases to evaluate
                </p>
              </div>
            )}
          </div>
        )}

        {hasTestSets &&
          testCaseMode === TEST_CASE_MODE.SELECT &&
          firstTestSetId &&
          !hasMultipleTestSets && (
            <TestCasePicker
              testSetId={firstTestSetId}
              selectedTestCaseIds={selectedTestCaseIds}
              onSelectedTestCaseIdsChange={(ids) =>
                onSelectedTestCaseIdsChange?.(ids)
              }
              disabled={disabled}
            />
          )}
      </EntitySelectionCard>
    </>
  )
}
